import React from 'react';

/**
 * Pagination - Reusable pagination component with item count display
 *
 * Displays: "Showing X-Y of Z items" + First/Previous/Next/Last buttons
 *
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} itemsPerPage - Number of items per page
 * @param {number} totalItems - Total number of items across all pages
 * @param {function} onPageChange - Callback when page changes: (newPage: number) => void
 * @param {boolean} loading - Optional: Disable buttons during loading
 * @param {string} itemLabel - Optional: Label for items (default: "items")
 */
const Pagination = ({
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  loading = false,
  itemLabel = 'items'
}) => {
  // Validate required props
  if (currentPage == null) throw new Error('Pagination requires currentPage prop');
  if (itemsPerPage == null) throw new Error('Pagination requires itemsPerPage prop');
  if (totalItems == null) throw new Error('Pagination requires totalItems prop');
  if (!onPageChange) throw new Error('Pagination requires onPageChange prop');

  // Calculate pagination values
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage >= totalPages;

  // Page change handlers
  const handleFirst = () => {
    if (!isFirstPage && !loading) {
      onPageChange(1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstPage && !loading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!isLastPage && !loading) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLast = () => {
    if (!isLastPage && !loading) {
      onPageChange(totalPages);
    }
  };

  // Button styling
  const getButtonClasses = (disabled) => {
    return `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      disabled
        ? 'bg-background-tertiary text-text-muted cursor-not-allowed'
        : 'bg-background-secondary text-text-primary hover:bg-background-tertiary'
    }`;
  };

  return (
    <div className="flex items-center justify-between pt-4 px-2">
      {/* Left: First + Previous */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleFirst}
          disabled={isFirstPage || loading}
          className={getButtonClasses(isFirstPage || loading)}
          aria-label="Go to first page"
        >
          First
        </button>
        <button
          onClick={handlePrevious}
          disabled={isFirstPage || loading}
          className={getButtonClasses(isFirstPage || loading)}
          aria-label="Go to previous page"
        >
          Previous
        </button>
      </div>

      {/* Center: Item count display */}
      <div className="text-sm text-text-muted">
        {totalItems === 0 ? (
          `No ${itemLabel}`
        ) : (
          `Showing ${startItem}-${endItem} of ${totalItems} ${itemLabel}`
        )}
      </div>

      {/* Right: Next + Last */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleNext}
          disabled={isLastPage || loading}
          className={getButtonClasses(isLastPage || loading)}
          aria-label="Go to next page"
        >
          Next
        </button>
        <button
          onClick={handleLast}
          disabled={isLastPage || loading}
          className={getButtonClasses(isLastPage || loading)}
          aria-label="Go to last page"
        >
          Last
        </button>
      </div>
    </div>
  );
};

export default Pagination;
